import {
  lazy,
  Suspense,
  ComponentType,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import { Routes, Outlet } from 'react-router';

import { SentryRoute as Route } from '@vk-hr-tek/core/monitoring';

import { WidgetReadyContext, WidgetReadySignal } from '@app/context';
import { ModulesNames, modules } from '@app/modules';
import { useIsWidget } from '@app/hooks';

import { RequireAuth, PageFallback } from './layout';

const Auth = lazy(async () => import('./auth'));
const Candidates = lazy(async () => import('./candidates'));
const Employees = lazy(async () => import('./employees'));
const Events = lazy(async () => import('./events'));
const Organization = lazy(async () => import('./organization'));
const Settings = lazy(async () => import('./settings'));
const Vacations = lazy(async () => import('./vacations'));
const Policy = lazy(async () => import('./policy'));
const Profile = lazy(async () => import('./user/Profile'));
const CreateCertificate = lazy(async () => import('./user/CreateCertificate'));
const Payslips = lazy(async () => import('./payslips'));
const Competencies = lazy(async () => import('./competencies'));
const BellNotifications = lazy(async () => import('./bell-notifications'));
const Dashboard = lazy(async () => import('./dashboard'));
const Absences = lazy(async () => import('./absences'));
const Personal = lazy(async () => import('./personal'));
const SideMenu = lazy(async () => import('./layout/SideMenu'));
const Header = lazy(async () => import('./layout/Header'));
const GeneralNews = lazy(async () => import('./general-news'));
const News = lazy(async () => import('./news'));
const Quiz = lazy(async () => import('./quiz'));
const Community = lazy(async () => import('./community'));
const Feed = lazy(async () => import('./feed'));
const Review = lazy(async () => import('./review'));
const SocialCategories = lazy(async () => import('./social-categories'));
const Surveys = lazy(async () => import('./surveys'));
const SocialAccess = lazy(async () => import('./social-access'));
const Search = lazy(async () => import('./search'));
const UserSettings = lazy(async () => import('./user-settings'));
const BusinessTrips = lazy(async () => import('./business-trips'));
const ProcessCatalog = lazy(async () => import('./process-catalog'));
const TargetAudience = lazy(async () => import('./target-audience'));
const Favorites = lazy(async () => import('./favorites'));

const resources: {
  module: ModulesNames;
  Component: ComponentType;
  role?: 'company' | 'general' | 'employee';
}[] = [
  {
    module: 'events',
    Component: Events,
  },
  {
    module: 'candidates',
    Component: Candidates,
  },
  {
    module: 'employees',
    Component: Employees,
  },
  {
    module: 'policy',
    Component: Policy,
  },
  {
    module: 'payslips',
    Component: Payslips,
  },
  {
    module: 'competencies',
    Component: Competencies,
  },
  {
    module: 'bell_notifications',
    Component: BellNotifications,
  },
  {
    module: 'settings',
    Component: Settings,
  },
  {
    module: 'vacations',
    Component: Vacations,
  },
  {
    module: 'absences',
    Component: Absences,
  },
  {
    module: 'sideMenu',
    Component: SideMenu,
  },
  {
    module: 'header',
    Component: Header,
  },
  {
    module: 'social_news',
    Component: News,
  },
  {
    module: 'social_categories',
    Component: SocialCategories,
  },
  {
    module: 'news',
    Component: GeneralNews,
  },
  {
    module: 'process_catalog',
    Component: ProcessCatalog,
  },
  {
    module: 'review',
    Component: Review,
  },
  {
    module: 'business_trips',
    Component: BusinessTrips,
  },
  {
    module: 'social_categories',
    Component: SocialCategories,
  },
  {
    module: 'social_access',
    Component: SocialAccess,
  },
  { module: 'quiz', Component: Quiz },
  {
    module: 'search',
    Component: Search,
  },
  { module: 'surveys', Component: Surveys },
  { module: 'community', Component: Community },
  { module: 'feed', Component: Feed },
  { module: 'target_audience', Component: TargetAudience },
  { module: 'favorites', Component: Favorites },
];

interface AppProps {
  page?: string;
}

function App({ page = 'all' }: AppProps) {
  const isWidget = useIsWidget();
  const widgetReady = useContext(WidgetReadyContext);
  const withWidgetReady = (children: ReactNode) =>
    widgetReady ? (
      <WidgetReadySignal nextFrame>{children}</WidgetReadySignal>
    ) : (
      children
    );

  const currentResources = useMemo(() => {
    const baseResources = [...resources];

    if (isWidget) {
      baseResources.push(
        {
          module: 'structure',
          Component: Organization,
          role: 'general',
        },
        {
          module: 'personal',
          Component: Personal,
        },
        {
          module: 'user',
          Component: Profile,
          role: 'employee',
        },
      );
    }

    return baseResources;
  }, [isWidget]);

  const routes = useMemo(() => {
    return page === 'all'
      ? currentResources.map((resource) => ({
          ...resource,
          path: modules[resource.module].basename,
          resource: modules[resource.module].resource,
        }))
      : currentResources
          .filter(({ module }) => {
            if (page === 'competencies') {
              return module === 'events' || module === page;
            }

            if (page === 'absences') {
              return (
                module === 'events' || module === 'vacations' || module === page
              );
            }

            if (page === 'profile') {
              return module === 'user';
            }

            if (page === 'organization') {
              return module === 'structure' || module === 'personal';
            }

            return module === page;
          })
          .map((resource, index, arr) => {
            const moduleName =
              resource.module === 'structure'
                ? 'organization'
                : resource.module;

            return {
              ...resource,
              path:
                arr.length > 1 && moduleName !== page
                  ? `/${modules[resource.module].basename}`
                  : '',
              resource: modules[resource.module].resource,
            };
          });
  }, [page, currentResources]);

  // 'all' роуты для основного приложения и Memory роутинга виджета SDK
  if (page === 'all') {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="auth">
            <Route index path="*" element={withWidgetReady(<Auth />)} />
          </Route>
          <Route
            path="create-certificate"
            element={
              <RequireAuth resource="User" role="employee">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route
              index
              path="*"
              element={withWidgetReady(<CreateCertificate />)}
            />
          </Route>
          <Route
            path="profile"
            element={
              <RequireAuth resource="User" role="employee">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Profile />)} />
          </Route>
          <Route
            path="news"
            element={
              <RequireAuth resource="News" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<GeneralNews />)} />
          </Route>
          <Route
            path="surveys"
            element={
              <RequireAuth resource="Surveys" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Surveys />)} />
          </Route>
          <Route
            path="community"
            element={
              <RequireAuth resource="Community" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Community />)} />
          </Route>
          <Route
            path="feed"
            element={
              <RequireAuth resource="Feed" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Feed />)} />
          </Route>
          <Route
            path="favorites"
            element={
              <RequireAuth resource="Favorites" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Favorites />)} />
          </Route>
          <Route
            path="notifications"
            element={
              <RequireAuth resource="User" role="employee">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route
              index
              path="*"
              element={withWidgetReady(<BellNotifications />)}
            />
          </Route>
          <Route
            path="organization"
            element={
              <RequireAuth resource="Organization" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Organization />)} />
          </Route>
          <Route
            path="process-catalog"
            element={
              <RequireAuth resource="ProcessCatalog" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route
              index
              path="*"
              element={withWidgetReady(<ProcessCatalog />)}
            />
          </Route>
          <Route
            path="personal"
            element={
              <RequireAuth resource="Personal" role="employee">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Personal />)} />
          </Route>
          <Route
            path="company"
            element={
              <RequireAuth resource="User" role="company">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route
              index
              path="*"
              element={
                <Routes>
                  {routes.map(({ path, resource, Component }) => {
                    return (
                      <Route
                        path={path}
                        key={path}
                        element={
                          <RequireAuth resource={resource} role="company">
                            <Outlet />
                          </RequireAuth>
                        }
                      >
                        <Route
                          index
                          path="*"
                          element={withWidgetReady(<Component />)}
                        />
                      </Route>
                    );
                  })}
                </Routes>
              }
            />
          </Route>
          <Route
            path="employee"
            element={
              <RequireAuth resource="User" role="employee">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route
              path="*"
              element={
                <Routes>
                  {routes.map(({ path, resource, Component }) => (
                    <Route
                      path={path}
                      key={path}
                      element={
                        <RequireAuth resource={resource} role="employee">
                          <Outlet />
                        </RequireAuth>
                      }
                    >
                      <Route
                        index
                        path="*"
                        element={withWidgetReady(<Component />)}
                      />
                    </Route>
                  ))}
                </Routes>
              }
            />
          </Route>
          <Route
            path="search"
            element={
              <RequireAuth resource="Search" role="general">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<Search />)} />
          </Route>
          <Route
            path="user_section"
            element={
              <RequireAuth resource="Dashboard" role="employee">
                <Outlet />
              </RequireAuth>
            }
          >
            <Route index path="*" element={withWidgetReady(<UserSettings />)} />
          </Route>
          <Route
            index
            path="*"
            element={
              <RequireAuth resource="User" role="employee">
                {withWidgetReady(<Dashboard />)}
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    );
  }

  // роуты для Basename роутинга виджета SDK
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="auth">
          <Route index path="*" element={withWidgetReady(<Auth />)} />
        </Route>
        <Route
          path="create-certificate"
          element={
            <RequireAuth resource="User" role="employee">
              <Outlet />
            </RequireAuth>
          }
        >
          <Route
            index
            path="*"
            element={withWidgetReady(<CreateCertificate />)}
          />
        </Route>
        {routes.map(({ path, role, resource, Component }) => {
          if (path === '')
            return (
              <Route
                index
                path="*"
                key={path}
                element={
                  <RequireAuth resource={resource} role={role}>
                    {withWidgetReady(<Component />)}
                  </RequireAuth>
                }
              />
            );

          return (
            <Route
              path={path}
              key={path}
              element={
                <RequireAuth resource={resource} role={role}>
                  <Outlet />
                </RequireAuth>
              }
            >
              <Route index path="*" element={withWidgetReady(<Component />)} />
            </Route>
          );
        })}
        <Route
          index
          path="*"
          element={
            <RequireAuth resource="User" role="employee">
              {withWidgetReady(<Dashboard />)}
            </RequireAuth>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
